import { Link } from "react-router-dom";
import { useTranslations } from "next-intl";
import { MapPin } from "lucide-react";
import {
  EMPLOYMENT_TYPE_LABEL_VI_SHORT,
  SALARY_TYPE_LABEL_VI,
  WORKER_POSITION_LABEL_VI,
  formatSalaryRange,
  type EmploymentType,
  type InsightTip,
  type SalaryType,
  type WorkerPosition,
} from "@chotca/config";
import type { Posting } from "@chotca/sdk";
import {
  daysUntilExpiry,
  derivePostingHealth,
  type PostingHealth,
} from "@/routes/postings/posting-health";

// Per-card status visuals. The left accent bar is the only mandatory
// affordance — it lets the employer parse a column of cards in one glance.
// Chip text on the right is the secondary signal and is short on purpose
// (employer is scanning, not reading).
const HEALTH_STYLE: Record<
  PostingHealth,
  { bar: string; chip: string; chipKey: string | null }
> = {
  stalled: {
    bar: "bg-ember",
    chip: "bg-ember/10 text-ember",
    chipKey: "healthStalled",
  },
  just_posted: {
    bar: "bg-sun",
    chip: "bg-sun-light text-sun-dark",
    chipKey: "healthJustPosted",
  },
  filled: {
    bar: "bg-lime",
    chip: "bg-lime/30 text-ink",
    chipKey: "healthFilled",
  },
  healthy: { bar: "bg-mist", chip: "", chipKey: null },
  closed: {
    bar: "bg-smoke",
    chip: "bg-smoke text-ink/60",
    chipKey: "healthClosed",
  },
};

// Picks the most useful single address line for the card. Postings can be
// attached to multiple branches (1..30); showing all of them on a list card
// is noise. We surface alias + district, and append a "+N chi nhánh" hint
// when there's more — the detail page shows the full list with map pins.
function formatAddressLine(
  p: Posting,
  branchMoreSuffix: (n: number) => string,
): string | null {
  const first = p.addresses?.[0];
  if (!first) return null;
  const more = (p.addresses?.length ?? 1) - 1;
  const base = first.alias
    ? `${first.alias} · ${first.district}`
    : first.address;
  return more > 0 ? `${base} · ${branchMoreSuffix(more)}` : base;
}

export function PostingHealthCard({
  posting,
  tips,
}: {
  posting: Posting;
  // Tips already filtered to those attached to this posting. Caller is
  // responsible for the matching so this component stays presentational.
  tips: InsightTip[];
}) {
  const t = useTranslations("postings");
  const health = derivePostingHealth(posting);
  const style = HEALTH_STYLE[health];
  const salary = formatSalaryRange(
    posting.salaryMin as unknown as number | bigint | string,
    posting.salaryMax as unknown as number | bigint | string,
    SALARY_TYPE_LABEL_VI[posting.salaryType as SalaryType],
  );
  const positionLabel =
    WORKER_POSITION_LABEL_VI[posting.position as WorkerPosition] ??
    posting.position;
  const empShort =
    EMPLOYMENT_TYPE_LABEL_VI_SHORT[posting.employmentType as EmploymentType] ??
    posting.employmentType;
  const address = formatAddressLine(posting, (n) =>
    t("branchMoreSuffix", { n }),
  );
  const applicants = posting.applicantStats?.total ?? 0;
  const remainingDays = daysUntilExpiry(posting);
  // Closed/expired tins shouldn't show "Hết hạn 3 ngày" — the chip on the
  // header already conveys their state. We hide the expiry line entirely.
  const showExpiry = health !== "closed";
  // Visual urgency for the expiry line. ≤2 days = ember to nudge action,
  // 0 or negative = also ember (it's already past or last-minute).
  const expiryUrgent = remainingDays !== null && remainingDays <= 2;

  return (
    <article className="relative overflow-hidden rounded-2xl border border-smoke bg-paper transition hover:border-ink/30 hover:shadow-sm">
      {/* Left accent bar — single source of visual urgency. */}
      <div className={`absolute inset-y-0 left-0 w-1 ${style.bar}`} />

      <Link
        to={`/postings/${posting.id}`}
        className="block px-4 py-4 pl-5 sm:px-5 sm:pl-6"
      >
        <div className="flex items-start gap-3 sm:gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] font-semibold uppercase tracking-wide text-ink/60">
              <span className="whitespace-nowrap">{positionLabel}</span>
              <span aria-hidden>·</span>
              <span className="whitespace-nowrap">{empShort}</span>
              {style.chipKey ? (
                <span
                  className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold tracking-wide ${style.chip}`}
                >
                  {t(style.chipKey).toUpperCase()}
                </span>
              ) : null}
            </div>

            <h3 className="mt-1 truncate text-base font-semibold text-ink">
              {posting.title}
            </h3>

            <div className="mt-1 text-sm font-semibold text-ink">{salary}</div>

            {address ? (
              <div className="mt-1 flex items-center gap-1 text-xs text-ink/60">
                <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden />
                {/* min-w-0 lets the span shrink below its content width so
                    truncate can kick in — a flex child won't ellipsis without
                    it, and a long address would otherwise widen the card. */}
                <span className="min-w-0 truncate">{address}</span>
              </div>
            ) : null}
          </div>

          {/* Big number on the right — applicants is the metric employer
              looks at first, so we make it the focal point. Time-remaining
              sits below it on its own line: postings live for 7 days so
              "Còn N ngày" tells the employer how much runway they have to
              decide / refresh. Goes ember when ≤2 days to act as a nudge. */}
          <div className="shrink-0 border-l border-smoke pl-3 text-right sm:pl-4">
            <div className="text-2xl font-extrabold leading-none text-ink sm:text-3xl">
              {applicants}
            </div>
            <div className="mt-1 whitespace-nowrap text-[10px] uppercase tracking-wider text-ink/50">
              {t("applicants")}
            </div>
            {showExpiry ? (
              <div
                className={`mt-2 whitespace-nowrap text-[11px] font-semibold ${
                  expiryUrgent ? "text-ember" : "text-ink/50"
                }`}
              >
                {remainingDays === null
                  ? t("expiryNoLimit")
                  : remainingDays < 0
                    ? t("expiryExpired")
                    : remainingDays === 0
                      ? t("expiryToday")
                      : t("expiryRemaining", { days: remainingDays })}
              </div>
            ) : null}
          </div>
        </div>
      </Link>

      {tips.length > 0 ? (
        <div className="mx-3 mb-3 space-y-2">
          {tips.map((tip) => (
            <PostingTipBanner key={tip.id} tip={tip} postingId={posting.id} />
          ))}
        </div>
      ) : null}
    </article>
  );
}

// A nudge attached to a specific posting. CTA is rendered as a link when we
// can map it to a route; otherwise the banner is informational only. We keep
// dismissal client-only for P1 (localStorage on `tip.id`) — covered by the
// parent list-client component, not here.
function PostingTipBanner({
  tip,
  postingId,
}: {
  tip: InsightTip;
  postingId: string;
}) {
  const t = useTranslations("postings");
  const ctaHref = tip.cta === "EDIT_POSTING" ? `/postings/${postingId}` : null;

  return (
    <div className="flex items-start gap-2 rounded-xl border border-sun-light/60 bg-sun-light/40 px-3 py-2 text-xs text-ink">
      <span className="mt-0.5 shrink-0" aria-hidden>
        💡
      </span>
      <div className="min-w-0 flex-1 wrap-break-word leading-snug">
        {tip.message}
      </div>
      {ctaHref ? (
        <Link
          to={ctaHref}
          className="shrink-0 rounded-md bg-ink px-2 py-1 text-[11px] font-semibold text-paper transition hover:bg-ink/90"
        >
          {t("tipEditPostingCta")}
        </Link>
      ) : null}
    </div>
  );
}
